<script lang="ts" setup>
import type { FormKitFrameworkContext } from '@formkit/core'
import { token } from '@formkit/utils'
import draggable from 'vuedraggable'
import { FormKitSchema } from '@formkit/vue'
import { toRaw } from 'vue'

const formkitProps = defineProps<{ context: FormKitFrameworkContext }>()
const context = formkitProps.context

const childrenSchema = computed(() => context.attrs.props?.childrenSchema)
const props = computed(() => ({
  ...context.attrs.props,
  hideAddButton: context.attrs.props?.hideAddButton || false,
  useNativeConfirm: context.attrs.props?.useNativeConfirm || false,
}))
const idKey = props.value.idKey || 'id'
const draggableProps: {
  onChange: <T>(context: FormKitFrameworkContext, values: Array<T>) => Array<T>
} = props.value.draggable
const titleBuilder:
  | string
  | (<T>(item: T, index: number, items: Array<T>) => string) = props.value.title

const optionsList = computed(
  () =>
    (typeof props.value.options === 'function'
      ? props.value.options?.()
      : props.value.options) || [],
)
const buttonTitle = computed(() => props.value.addButtonTitle || 'Add Item')

const createListItem = (value, index, newItem?: boolean) => ({
  title: computed(() => {
    if (typeof titleBuilder === 'function') {
      return titleBuilder(value, index, context._value)
    }
    return (
      titleBuilder ||
      optionsList.value.find(({ id }) =>
        typeof idKey === 'string'
          ? String(value?.[idKey]) === String(id)
          : idKey(value) === id,
      )?.name
    )
  }),
  opened: newItem || null,
  value: {
    ...value,
    channelId: context.attrs.props?.channelId,
  },
  key: token(),
})

let hasInitialized = false

const items = ref(
  formkitProps.context._value.map((value, index) =>
    createListItem(value, index),
  ),
)

hasInitialized = true

/** Rows added/removed on the parent model (e.g. quick-fill) must refresh local `items` — init only ran once. */
function rowKeysSignature(rows: unknown[], idK: string) {
  return rows
    .map((x) => String(toRaw(x)?.[idK] ?? ''))
    .sort()
    .join(',')
}

function syncItemsFromContextIfStructureChanged() {
  const idK = props.value.idKey || 'id'
  const newVal = context.value || []
  const newRows = [...newVal]
  const currentValueRows = items.value.map((it) => it.value)

  if (
    newRows.length === items.value.length &&
    rowKeysSignature(newRows, idK) === rowKeysSignature(currentValueRows, idK)
  ) {
    return
  }

  const prevByKey = new Map(
    items.value.map((it) => [String(it.value[idK]), it]),
  )

  items.value = newRows.map((value, index) => {
    const merged = {
      ...value,
      channelId: context.attrs.props?.channelId,
    }
    const prev = prevByKey.get(String(merged[idK]))
    if (prev) {
      prev.value = merged
      return prev
    }
    return createListItem(merged, index, hasInitialized)
  })
}

watch(
  () => (Array.isArray(context.value) ? context.value.length : 0),
  () => syncItemsFromContextIfStructureChanged(),
)

onMounted(() => {
  nextTick(() => syncItemsFromContextIfStructureChanged())
})

const activeOptionsList = computed(() =>
  props.value?.duplicates
    ? optionsList.value
    : optionsList.value.filter(
        (item: any) =>
          !items.value.some(
            ({ value }: any) => String(item.id) === String(value?.[idKey]),
          ),
      ),
)

const allLanguagesAdded = computed(() =>
  !props.value.allowDuplicates && optionsList.value.length > 0
    ? items.value.length >= optionsList.value.length
    : false,
)

const hasValidationErrors = computed(() => {
  return context.state.valid === false
})

const validationMessage = computed(() => {
  if (hasValidationErrors.value && items.value.length === 0) {
    return context.messages?.[0]?.value || 'Please provide at least one item'
  }
  return null
})

const onCreate = async (value: any) => {
  console.log('onCreate props:', {
    contextProps: context.attrs.props,
    channelId: context.attrs.props?.channelId,
  })

  items.value.push(
    createListItem(
      {
        [idKey]: isNaN(+value.id) ? value.id : +value.id,
        channelId: context.attrs.props?.channelId,
      },
      items.value.length,
      true,
    ),
  )

  await context.node.input(items.value.map((item) => item.value))
}

context.node.on('child', ({ payload: node }: any) => {
  node?.on('remove', async ({ payload: key }) => {
    let shouldDelete = true
    if (props.value.showConfirmDialog !== false) {
      if (props.value.useNativeConfirm) {
        shouldDelete = window.confirm(
          "Item will be deleted. This operation can't be undone.",
        )
      } else {
        const { isCanceled } = await confirmDialog.reveal({
          description: "Item will be deleted. This operation can't be undone.",
          type: 'danger',
        })
        shouldDelete = !isCanceled
      }
    }

    if (!shouldDelete) {
      return
    }
    const value = items.value.find((item) => item.key === key)?.value
    items.value = items.value.filter((item) => item.key !== key)
    if (!value.id) {
      context.node.remove(key)
      return
    }
    const newValues = context.value.filter((item) => item.id !== value.id)
    await context.node.input(newValues)
  })
})

const onDraggableChange = (e) => {
  if (e && e.oldIndex !== e.newIndex) {
    const values = [...context.value]

    const [movedItem] = values.splice(e.oldIndex, 1)
    values.splice(e.newIndex, 0, movedItem)
    context.value = values

    context.node.input(values)

    if (draggableProps.onChange) {
      draggableProps.onChange(context, values)
    }
  }
}
</script>

<template>
  <div>
    <label
      v-if="context.label"
      class="formkit-label block mb-1 font-medium text-sm formkit-invalid:text-red-500 text-gray-700"
      >{{ context.label }}</label
    >
    <div
      :class="{
        'border-2 border-dashed border-red-300 rounded-lg p-4':
          hasValidationErrors && items.length === 0,
      }"
      class="grid gap-2"
    >
      <draggable
        v-if="draggableProps"
        :list="items"
        class="list-group grid gap-2 flex-grow"
        item-key="title"
        @end="onDraggableChange"
      >
        <template #item="{ element, index }">
          <div>
            <FormKit
              :id="`${element.key}`"
              :key="`${element.key}`"
              :label="element.title"
              :props="{
                ...context.attrs.props,
                channelId: context.attrs.props?.channelId,
                type: context.attrs.props?.type,
                opened: element.opened || context.attrs?.props?.opened,
              }"
              type="repeaterItem"
            >
              <FormKitSchema
                v-if="childrenSchema && props.isolatedScope"
                :schema="childrenSchema"
                :data="{
                  value: context.value?.[index] || element.value,
                  index,
                  item: element,
                }"
              />
              <slot
                v-else-if="props.isolatedScope"
                :item="element"
                :index="index"
                :value="context.value?.[index] || element.value"
              />
              <slot v-else />
            </FormKit>
          </div>
        </template>
      </draggable>
      <FormKit
        v-for="(element, index) in items"
        v-else
        :id="element.key"
        :key="element.key"
        :label="element.title"
        :props="{
          ...context.attrs.props,
          channelId: context.attrs.props?.channelId,
          opened: element.opened || context.attrs.props?.opened,
        }"
        type="repeaterItem"
      >
        <FormKitSchema
          v-if="childrenSchema && props.isolatedScope"
          :schema="childrenSchema"
          :data="{
            value: context.value?.[index] || element.value,
            index,
            item: element,
          }"
        />
        <slot
          v-else-if="props.isolatedScope"
          :item="element"
          :index="index"
          :value="context.value?.[index] || element.value"
        />
        <slot v-else />
      </FormKit>

      <p v-if="validationMessage" class="text-sm text-red-500 mt-1">
        {{ validationMessage }}
      </p>

      <div
        v-if="!props.hideAddButton && !allLanguagesAdded"
        :class="{ 'pt-2': items.length }"
        class="flex justify-start pb-2 mt-1"
      >
        <Combobox
          v-if="context.attrs.props?.options"
          :items="activeOptionsList"
          @update:model-value="onCreate"
        >
          <Button
            class="flex items-center"
            icon="i-heroicons-solid-plus"
            variant="gray"
          >
            {{ buttonTitle }}
          </Button>
        </Combobox>
        <Button
          v-else
          class="flex items-center"
          icon="i-heroicons-solid-plus"
          variant="gray"
          @click="onCreate"
        >
          {{ buttonTitle }}
        </Button>
      </div>
    </div>
  </div>
</template>
